/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples — copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  activateTurbo,
  adminActivityActions,
  adminActivityAnalytics,
  adminArchiveHousehold,
  adminCheckHub,
  adminCreateHousehold,
  adminCreateWifiLink,
  adminDeleteHaToken,
  adminDeleteHouseholdDocument,
  adminDeleteUser,
  adminDeleteWifiPassword,
  adminExportHousehold,
  adminGetHousehold,
  adminGetUser,
  adminGetWifiLink,
  adminHeatingGroups,
  type AdminHouseholdCreate,
  type AdminHouseholdUpdate,
  adminImportHousehold,
  adminInviteUser,
  adminListActivity,
  adminListHouseholds,
  adminListUsers,
  adminRegenerateAccessCode,
  adminResetOnboarding,
  adminRevealHouseholdSecrets,
  adminRevokeWifiLink,
  adminUnarchiveHousehold,
  adminUpdateHousehold,
  adminUpdatePreconfig,
  adminUpdateUser,
  adminUploadHouseholdDocument,
  applyHeatingMapping,
  applyWindowMapping,
  askAssistant,
  type AssistantAskRequest,
  changePassword,
  completeOnboardingStep,
  confirmWifiLink,
  diagnoseGateways,
  fireHeatingZone,
  type GatewayProvisionRequest,
  getActivity,
  getAirthingsStatus,
  getHealth,
  getHubStatus,
  getMe,
  getMeDetail,
  getOnboardingHousehold,
  getOnboardingState,
  getVentilationState,
  getWifiLink,
  getWindowsStatus,
  type HeatingMappingEntry,
  listAirSensors,
  listGateways,
  listHeatingZones,
  login,
  loginWithCode,
  logout,
  mergeHeatingRooms,
  type PreconfigUpdate,
  provisionGateway,
  recordIssue,
  type RecordIssueRequest,
  requestOnboardingLink,
  rescanWindows,
  type RoomMergeRequest,
  type SetupImport,
  setWindowPosition,
  signup,
  skipOnboardingStep,
  submitWifiLink,
  type TurboRequest,
  updateProfile,
  type WifiLinkSubmitRequest,
  type WindowMappingEntry,
} from "@/lib/api/generated";

// ─── Public queries / mutations ──────────────────────────────────────────

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const { data } = await getHealth();
      return data;
    },
  });
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await getMe();
      return data;
    },
    // Don't retry on a 401 — ProtectedRoute will redirect to /login.
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string; password: string; rememberMe?: boolean }) => {
      const { data } = await login({
        body: {
          email: vars.email,
          password: vars.password,
          remember_me: vars.rememberMe ?? false,
        },
      });
      return data;
    },
    onSuccess: () => {
      // A login can follow a session that ended WITHOUT the in-app logout
      // (expired/cleared cookie), so the cache may still hold the previous
      // identity's data — and a different user logging in on the same tab
      // would briefly see it. Clear everything, mirroring useLogout; the new
      // session refetches what it needs (including "me").
      queryClient.clear();
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear the entire cache so no stale per-user data leaks across sessions.
      queryClient.clear();
    },
  });
}

export function useSignup() {
  return useMutation({
    mutationFn: async (vars: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
    }) => {
      const { data } = await signup({
        body: {
          email: vars.email,
          password: vars.password,
          first_name: vars.firstName,
          last_name: vars.lastName,
        },
      });
      return data;
    },
  });
}

// ─── Profile ─────────────────────────────────────────────────────────────

export function useMeDetail() {
  return useQuery({
    queryKey: ["me", "detail"],
    queryFn: async () => {
      const { data } = await getMeDetail();
      return data;
    },
    retry: false,
  });
}

export function useActivity() {
  return useQuery({
    queryKey: ["activity"],
    queryFn: async () => {
      const { data } = await getActivity();
      return data ?? [];
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { firstName: string; lastName: string }) => {
      await updateProfile({
        body: { first_name: vars.firstName, last_name: vars.lastName },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (vars: { currentPassword: string; newPassword: string }) => {
      await changePassword({
        body: { current_password: vars.currentPassword, new_password: vars.newPassword },
      });
    },
  });
}

// ─── Onboarding ──────────────────────────────────────────────────────────

export function useOnboardingState() {
  return useQuery({
    queryKey: ["onboarding", "state"],
    queryFn: async () => {
      const { data } = await getOnboardingState();
      return data;
    },
    // Don't retry — a 401 (no session) or 404 (no household) must fail fast
    // so the page falls back to the email-entry screen instead of spinning.
    retry: false,
  });
}

export function useRequestOnboardingLink() {
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      const { data } = await requestOnboardingLink({ body: { email: vars.email } });
      return data;
    },
  });
}

export function useLoginWithCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { code: string }) => {
      const { data } = await loginWithCode({ body: { code: vars.code } });
      return data;
    },
    onSuccess: () => {
      // The session cookie is now set — refetch the flow state so the page
      // advances from code entry into onboarding.
      void queryClient.invalidateQueries({ queryKey: ["onboarding", "state"] });
    },
  });
}

export function useCompleteOnboardingStep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { stepKey: string; payload?: Record<string, unknown> }) => {
      const { data } = await completeOnboardingStep({
        path: { step_key: vars.stepKey },
        body: { payload: vars.payload ?? {} },
      });
      return data;
    },
    onSuccess: (data) => {
      // The endpoint returns the fresh onboarding state — write it straight
      // into the cache; no refetch needed. If the body is somehow missing,
      // fall back to a refetch so the UI can't silently stall.
      if (data) {
        queryClient.setQueryData(["onboarding", "state"], data);
      } else {
        void queryClient.invalidateQueries({ queryKey: ["onboarding", "state"] });
      }
    },
  });
}

/**
 * Mark a step skipped so a household blocked on it can carry on.
 *
 * Shares the completion hook's cache handling: the endpoint answers with the
 * fresh state, so the flow re-routes off the skipped step immediately.
 */
export function useSkipOnboardingStep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { stepKey: string; reason?: string }) => {
      const { data } = await skipOnboardingStep({
        path: { step_key: vars.stepKey },
        body: { reason: vars.reason ?? "" },
      });
      return data;
    },
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData(["onboarding", "state"], data);
      } else {
        void queryClient.invalidateQueries({ queryKey: ["onboarding", "state"] });
      }
    },
  });
}

export function useOnboardingHousehold() {
  return useQuery({
    queryKey: ["onboarding", "household"],
    queryFn: async () => {
      const { data } = await getOnboardingHousehold();
      return data;
    },
    retry: false,
  });
}

export function useGateways(options?: { enabled?: boolean; refetchInterval?: number }) {
  return useQuery({
    queryKey: ["onboarding", "gateways"],
    queryFn: async () => {
      const { data } = await listGateways();
      return data ?? [];
    },
    retry: false,
    // Steps poll this while waiting for a gateway entry to reach "loaded".
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval,
  });
}

export function useProvisionGateway() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: GatewayProvisionRequest) => {
      const { data } = await provisionGateway({ body });
      return data;
    },
    onSuccess: () => {
      // A new entry (or a MAC written back to a device) changes both views.
      void queryClient.invalidateQueries({ queryKey: ["onboarding", "gateways"] });
      void queryClient.invalidateQueries({ queryKey: ["onboarding", "household"] });
    },
  });
}

/**
 * Read-only fault diagnosis for both USR gateways.
 *
 * Not polled: the floor-heating and ventilation connecting stages enable it
 * once a connector has failed a few times, so it runs once in the background
 * while the screen keeps retrying, and the verdict is already there by the
 * time the household is offered the assistant or a skip.
 */
export function useGatewayDiagnosis(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["onboarding", "gateways", "diagnosis"],
    queryFn: async () => {
      const { data } = await diagnoseGateways();
      return data;
    },
    retry: false,
    enabled: options?.enabled,
  });
}

/**
 * Record a connector step giving up, for the end-of-onboarding report.
 *
 * Upsert-safe on the backend, so a screen can fire this every time the
 * household skips a failing step without worrying about duplicates.
 */
export function useRecordIssue() {
  return useMutation({
    mutationFn: async (body: RecordIssueRequest) => {
      const { data } = await recordIssue({ body });
      return data;
    },
  });
}

export function useHubStatus(options?: { enabled?: boolean; refetchInterval?: number }) {
  return useQuery({
    queryKey: ["onboarding", "hub-status"],
    queryFn: async () => {
      const { data } = await getHubStatus();
      return data;
    },
    retry: false,
    // The hub-setup screen polls while the hub boots; reachable=false is a
    // normal (200) response, so polling just keeps refetching.
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval,
  });
}

export function useHeatingZones(options?: { enabled?: boolean; refetchInterval?: number }) {
  return useQuery({
    queryKey: ["onboarding", "heating-zones"],
    queryFn: async () => {
      const { data } = await listHeatingZones();
      return data ?? [];
    },
    retry: false,
    // Circuit identification polls this to watch valve_open flip per zone.
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval,
  });
}

export function useFireHeatingZone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { zone: number; temperature?: number }) => {
      const { data } = await fireHeatingZone({
        path: { zone: vars.zone },
        body: vars.temperature !== undefined ? { temperature: vars.temperature } : {},
      });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["onboarding", "heating-zones"] });
    },
  });
}

/**
 * Merge the rooms one thermostat heats into a single room, after the resident
 * confirmed it on the heating step. Record and hub change together on the
 * backend; here the state refetches so every later screen sees one room.
 */
export function useMergeHeatingRooms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: RoomMergeRequest) => {
      const { data } = await mergeHeatingRooms({ body });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["onboarding", "state"] });
      void queryClient.invalidateQueries({ queryKey: ["onboarding", "household"] });
    },
  });
}

export function useApplyHeatingMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (mappings: HeatingMappingEntry[]) => {
      const { data } = await applyHeatingMapping({ body: { mappings } });
      return data;
    },
    onSuccess: () => {
      // Rooms now carry zone-linked heating devices.
      void queryClient.invalidateQueries({ queryKey: ["onboarding", "state"] });
      void queryClient.invalidateQueries({ queryKey: ["onboarding", "household"] });
    },
  });
}

export function useVentilationState(options?: {
  enabled?: boolean;
  refetchInterval?: number;
  /** A token for the current connect attempt; a new one starts an empty cache. */
  attempt?: number;
}) {
  return useQuery({
    queryKey: ["onboarding", "ventilation", options?.attempt ?? 0],
    queryFn: async () => {
      const { data } = await getVentilationState();
      return data ?? [];
    },
    retry: false,
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval,
    // The connect checklist gates "Ventilation online" on this. A reading
    // cached from an earlier attempt must never stand in for the current one,
    // so this query is always stale and refetches the moment it is enabled.
    staleTime: 0,
    refetchOnMount: "always",
    // And nothing is kept once the screen unmounts, so a later attempt
    // starts with no data at all rather than a reading from the last one.
    gcTime: 0,
  });
}

export function useActivateTurbo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body?: TurboRequest) => {
      const { data } = await activateTurbo({ body: body ?? {} });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["onboarding", "ventilation"] });
    },
  });
}

export function useAirthingsStatus(options?: { enabled?: boolean; refetchInterval?: number }) {
  return useQuery({
    queryKey: ["onboarding", "airthings-status"],
    queryFn: async () => {
      const { data } = await getAirthingsStatus();
      return data;
    },
    retry: false,
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval,
  });
}

export function useWindowsStatus(options?: { enabled?: boolean; refetchInterval?: number }) {
  return useQuery({
    queryKey: ["onboarding", "windows-status"],
    queryFn: async () => {
      const { data } = await getWindowsStatus();
      return data;
    },
    retry: false,
    // The windows step polls this while the connector comes online, then reads
    // it again to list the actuators it exposes.
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval,
  });
}

export function useRescanWindows() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await rescanWindows();
      return data;
    },
    onSuccess: (data) => {
      // The response IS the fresh status — seed the cache with it.
      if (data) {
        queryClient.setQueryData(["onboarding", "windows-status"], data);
      } else {
        void queryClient.invalidateQueries({ queryKey: ["onboarding", "windows-status"] });
      }
    },
  });
}

export function useSetWindowPosition() {
  return useMutation({
    mutationFn: async (vars: { entityId: string; position?: number }) => {
      const { data } = await setWindowPosition({
        body: { entity_id: vars.entityId, position: vars.position ?? 25 },
      });
      return data;
    },
  });
}

export function useApplyWindowMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (mappings: WindowMappingEntry[]) => {
      const { data } = await applyWindowMapping({ body: { mappings } });
      return data;
    },
    onSuccess: () => {
      // Rooms now carry actuator-linked windows.
      void queryClient.invalidateQueries({ queryKey: ["onboarding", "state"] });
      void queryClient.invalidateQueries({ queryKey: ["onboarding", "household"] });
      void queryClient.invalidateQueries({ queryKey: ["onboarding", "windows-status"] });
    },
  });
}

export function useAirSensors(options?: { enabled?: boolean; refetchInterval?: number }) {
  return useQuery({
    queryKey: ["onboarding", "air-sensors"],
    queryFn: async () => {
      const { data } = await listAirSensors();
      return data ?? [];
    },
    retry: false,
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval,
  });
}

// ─── Admin ───────────────────────────────────────────────────────────────

export function useAdminUsers() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => {
      const { data } = await adminListUsers();
      return data ?? [];
    },
  });
}

export function useAdminUser(userId: string | null) {
  return useQuery({
    queryKey: ["admin", "users", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      const { data } = await adminGetUser({ path: { user_id: userId } });
      return data;
    },
    enabled: !!userId,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      userId: string;
      firstName?: string;
      lastName?: string;
      role?: string;
    }) => {
      await adminUpdateUser({
        path: { user_id: vars.userId },
        body: {
          first_name: vars.firstName,
          last_name: vars.lastName,
          role: vars.role,
        },
      });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users", vars.userId] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      await adminDeleteUser({ path: { user_id: vars.userId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      const { data } = await adminInviteUser({ body: { email: vars.email } });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

// ─── Admin activity ──────────────────────────────────────────────────────

export interface AdminActivityParams {
  search?: string;
  action?: string;
  userId?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export function useAdminActivity(params: AdminActivityParams = {}) {
  return useQuery({
    queryKey: ["admin", "activity", params],
    queryFn: async () => {
      const { data } = await adminListActivity({
        query: {
          search: params.search,
          action: params.action,
          user_id: params.userId,
          sort_by: params.sortBy ?? "created_at",
          sort_dir: params.sortDir ?? "desc",
          page: params.page ?? 1,
          page_size: params.pageSize ?? 25,
        },
      });
      return data;
    },
    // Keep the previous page on screen while fetching the next so the table
    // doesn't flash empty between paginations / filter changes.
    placeholderData: (prev) => prev,
  });
}

export function useAdminActivityAnalytics(days = 30) {
  return useQuery({
    queryKey: ["admin", "activity", "analytics", days],
    queryFn: async () => {
      const { data } = await adminActivityAnalytics({ query: { days } });
      return data;
    },
  });
}

export function useAdminActivityActions() {
  return useQuery({
    queryKey: ["admin", "activity", "actions"],
    queryFn: async () => {
      const { data } = await adminActivityActions();
      return data ?? [];
    },
  });
}

// ─── Admin households ─────────────────────────────────────────────────────

export function useAdminHouseholds(archived?: boolean) {
  return useQuery({
    queryKey: ["admin", "households", { archived: archived ?? false }],
    queryFn: async () => {
      const { data } = await adminListHouseholds({ query: { archived } });
      return data ?? [];
    },
  });
}

export function useAdminHousehold(householdId: string | null) {
  return useQuery({
    queryKey: ["admin", "households", householdId],
    queryFn: async () => {
      if (!householdId) {
        return null;
      }
      const { data } = await adminGetHousehold({ path: { household_id: householdId } });
      return data;
    },
    enabled: !!householdId,
  });
}

/**
 * The thermostat groups on a household's Wavin, read live from its hub.
 *
 * Live rather than from the stored record: the grouping changes whenever
 * someone re-scans or re-pairs, and a stale card claiming a house is fine
 * would be worse than no card. The endpoint answers `available: false` with a
 * reason instead of failing, so a hub that is off does not error the page.
 */
export function useAdminHeatingGroups(householdId: string | null) {
  return useQuery({
    queryKey: ["admin", "households", householdId, "heating-groups"],
    queryFn: async () => {
      if (!householdId) {
        return null;
      }
      const { data } = await adminHeatingGroups({ path: { household_id: householdId } });
      return data ?? null;
    },
    enabled: !!householdId,
  });
}

/**
 * What this household's hub says when called with the token on record.
 *
 * Keyed under the household so saving a token invalidates it: the save is the
 * moment the answer changes. Three outcomes rather than a boolean, because
 * "the hub refused the token" is a record to correct and "the hub said
 * nothing" may be a visit, and the card used to show both as a green tick.
 */
export function useAdminHubCheck(householdId: string | null) {
  return useQuery({
    queryKey: ["admin", "households", householdId, "hub-check"],
    queryFn: async () => {
      if (!householdId) {
        return null;
      }
      const { data } = await adminCheckHub({ path: { household_id: householdId } });
      return data ?? null;
    },
    enabled: !!householdId,
  });
}

export function useCreateHousehold() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: AdminHouseholdCreate) => {
      const { data } = await adminCreateHousehold({ body: vars });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "households"] });
    },
  });
}

export function useUpdateHousehold() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; body: AdminHouseholdUpdate }) => {
      const { data } = await adminUpdateHousehold({
        path: { household_id: vars.id },
        body: vars.body,
      });
      return data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "households"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "households", vars.id] });
    },
  });
}

export function useUpdatePreconfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; body: PreconfigUpdate }) => {
      const { data } = await adminUpdatePreconfig({
        path: { household_id: vars.id },
        body: vars.body,
      });
      return data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "households"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "households", vars.id] });
    },
  });
}

export function useImportHousehold() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: SetupImport) => {
      const { data } = await adminImportHousehold({ body: vars });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "households"] });
    },
  });
}

export function useExportHousehold() {
  return useMutation({
    mutationFn: async (householdId: string) => {
      const { data } = await adminExportHousehold({ path: { household_id: householdId } });
      return data;
    },
  });
}

export function useRegenerateAccessCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string }) => {
      const { data } = await adminRegenerateAccessCode({ path: { household_id: vars.id } });
      return data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "households", vars.id] });
    },
  });
}

export function useArchiveHousehold() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string }) => {
      const { data } = await adminArchiveHousehold({ path: { household_id: vars.id } });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "households"] });
    },
  });
}

export function useUnarchiveHousehold() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string }) => {
      const { data } = await adminUnarchiveHousehold({ path: { household_id: vars.id } });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "households"] });
    },
  });
}

/**
 * Fetch a household's stored secrets (plaintext) on demand — used by the
 * password-reveal toggles. Admin-only; each call is logged server-side, so it
 * is a mutation (an explicit action) rather than a cached query.
 */
export function useRevealHouseholdSecrets() {
  return useMutation({
    mutationFn: async (vars: { id: string }) => {
      const { data } = await adminRevealHouseholdSecrets({ path: { household_id: vars.id } });
      return data;
    },
  });
}

export function useResetOnboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string }) => {
      const { data } = await adminResetOnboarding({ path: { household_id: vars.id } });
      return data;
    },
    onSuccess: (_data, vars) => {
      // Progress rows were wiped and the status may have flipped — refresh both
      // the detail view and the list (its completed-step count is now stale).
      void queryClient.invalidateQueries({ queryKey: ["admin", "households", vars.id] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "households"] });
    },
  });
}

export function useUploadHouseholdDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      householdId: string;
      file: Blob | File;
      title: string;
      kind: string;
    }) => {
      const { data } = await adminUploadHouseholdDocument({
        path: { household_id: vars.householdId },
        body: { file: vars.file, title: vars.title, kind: vars.kind },
      });
      return data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "households", vars.householdId] });
    },
  });
}

export function useDeleteHouseholdDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { householdId: string; documentId: string }) => {
      await adminDeleteHouseholdDocument({
        path: { household_id: vars.householdId, document_id: vars.documentId },
      });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "households", vars.householdId] });
    },
  });
}

/**
 * Ask the onboarding assistant. The backend gathers the household's live
 * hardware state for the given step, so `stepKey`/`stage` are what make the
 * answer specific rather than generic.
 *
 * Not cached: every ask re-reads hardware state on purpose, so "look again"
 * works by simply asking again.
 */
export function useAskAssistant() {
  return useMutation({
    mutationFn: async (body: AssistantAskRequest) => {
      const { data } = await askAssistant({ body });
      return data;
    },
  });
}

// ─── Wi-Fi credential sharing links ──────────────────────────────────────

/**
 * Resolve a sharing-link token to its masked hint and status.
 *
 * Public: no session is involved, the token in the URL is the only
 * credential. `retry: false` so an unknown token surfaces its 404 straight
 * away instead of the page sitting on a skeleton through three retries.
 */
export function useWifiLink(token: string | undefined) {
  return useQuery({
    queryKey: ["wifi-link", token],
    queryFn: async () => {
      const { data } = await getWifiLink({ path: { token: token as string } });
      return data;
    },
    enabled: Boolean(token),
    retry: false,
  });
}

export function useConfirmWifiLink() {
  return useMutation({
    mutationFn: async (vars: { token: string; identityAnswer: string }) => {
      const { data } = await confirmWifiLink({
        path: { token: vars.token },
        body: { identity_answer: vars.identityAnswer },
      });
      return data;
    },
  });
}

export function useSubmitWifiLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { token: string; body: WifiLinkSubmitRequest }) => {
      const { data } = await submitWifiLink({
        path: { token: vars.token },
        body: vars.body,
      });
      return data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["wifi-link", vars.token] });
    },
  });
}

export function useAdminWifiLink(householdId: string | null) {
  return useQuery({
    queryKey: ["admin", "households", householdId, "wifi-link"],
    queryFn: async () => {
      const { data } = await adminGetWifiLink({
        path: { household_id: householdId as string },
      });
      return data;
    },
    enabled: Boolean(householdId),
  });
}

export function useCreateWifiLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string }) => {
      const { data } = await adminCreateWifiLink({ path: { household_id: vars.id } });
      return data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "households", vars.id, "wifi-link"],
      });
    },
  });
}

export function useRevokeWifiLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string }) => {
      const { data } = await adminRevokeWifiLink({ path: { household_id: vars.id } });
      return data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "households", vars.id, "wifi-link"],
      });
    },
  });
}

/**
 * Take the hub token off a household.
 *
 * Saving an empty field means "keep the one you have", so this is the only way
 * a wrong token comes off a record.
 */
export function useDeleteHaToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string }) => {
      const { data } = await adminDeleteHaToken({ path: { household_id: vars.id } });
      return data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "households"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "households", vars.id] });
    },
  });
}

export function useDeleteWifiPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string }) => {
      const { data } = await adminDeleteWifiPassword({ path: { household_id: vars.id } });
      return data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "households"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "households", vars.id] });
    },
  });
}
